import apiClient from '@/api/api';
import { manageApiCall, ManagedApiCall } from '@/api/api-manager';
import { getBaFilters, getBaQuery } from '@/api/surveys/fetch-ba-query';
import { getGeojsonByFilter, getGeojsonByIds } from '@/api/surveys/get-all-geojsons';
import { getSurveysByTemplate } from '@/api/surveys/get-surveys';
import { Fragenblock } from '@/models/ba/Fragenblock';
import { BestandsaufnahmeFlatJson, BestandsaufnahmeJson, HzbaStatusCode, HzbaStatusKey } from '@/models/ba/interfaces/IBestandsaufnahme';
import ErweitertesMedium from '@/models/erweitertes-medium.model';
import Unterschrift from '@/models/hzba/unterschrift.model';
import Immobilie from '@/models/immobilie.model';
import Localization from '@/models/localization.model';
import User from '@/models/user';
import { DateFilters } from '@/store/store.types';
import { SelectModeState } from '@/types/app/survey-types';
import { isDateInRange } from '@/utilities/compare-helper';
import { surveysPageSize } from '@/utilities/constants';
import { downloadFile } from '@/utilities/download-helper';
import { getFilename, getGeoJsonExportConfig } from '@/utilities/export-helper';
import { Monitoring } from '@/utilities/monitoring';
import { copyBaAndMergeWithImmobilie, sortArrayByProperty } from "@/utilities/sortArrayByProperty";
import { presentToast } from '@/utilities/toast-helper';
import { Network } from '@capacitor/network';
import { Feature, featureCollection } from '@turf/turf';
import { Model } from '@vuex-orm/core';
import axios, { CancelTokenSource } from 'axios';
import { APhoto } from '../../photo/a-photo.model';
import StrapiMedia from '../../photo/strapi-media.model';
import Bestandsaufnahme from '../Bestandsaufnahme';
import { searchImmobilieByTerm } from '@/models/immobilie/search';
export default class BestandsaufnahmeModel extends Model implements BestandsaufnahmeFlatJson {
  /**
   * Vuex ORM entity name
   */
  static entity = 'bestandsaufnahmes';

  /**
   * Backend variable definitions
   */
  public id!: number;
  public bilder!: APhoto[];
  public malus!: string;
  public malusColor!: string;
  public kommentar!: string;
  public begehungsdatum!: string;
  public sichtungsdatum!: string;
  public berichts!: ErweitertesMedium[];
  public mangellists!: string;
  public archive!: StrapiMedia;
  public matterportLink?: string;
  public immobilie!: number;
  public status!: HzbaStatusKey;
  public unterschrifts?: Unterschrift[];
  public auftraggeber!: string;
  public technProjektsteuerungName!: string;
  public technProjektsteuerungUnterschrift!: APhoto[];
  public reihenfolge!: string;
  public freigabeDatum!: string;
  public fazit!: string;
  public createdAt!: string;
  public updatedAt!: string;
  public createdBy!: string;
  public gutachter!: any;
  public updatedBy!: User;
  public bearbeitenderNutzer?: User;
  public bearbeitendesGeraetId?: string;
  public fragenblocks?: Fragenblock[];
  public name!: Localization;
  public nameMehrzahl!: Localization;
  public nameBericht!: Localization;
  public identifier!: string;
  public surveyType!: string;
  public isLocal!: boolean; // denotes BA is changed and not synchronized with Backend
  public hasUnsavedChanges!: boolean;
  public hasLocalImages!: boolean; // denotes BA is changed and not synchronized with Backend
  public isDownloaded!: boolean; // denotes user chose BA to be downloaded locally
  public areMinimumFragenblocksSet?: boolean;
  public removedMangelsFromMigrations?: any;
  public vorlage_bestandsaufnahme?: any;
  public geolocationLon!: number;
  public geolocationLat!: number;

  /**
   * Default values
   */
  static fields() {
    return {
      id: this.attr(null),
      bilder: this.attr(null),
      malus: this.attr(null),
      malusColor: this.attr(null),
      name: this.attr(null),
      nameMehrzahl: this.attr(null),
      nameBericht: this.attr(null),
      identifier: this.attr(null),
      kommentar: this.attr(null),
      begehungsdatum: this.attr(null),
      sichtungsdatum: this.attr(null),
      archive: this.attr(null),
      matterportLink: this.attr(null),
      status: this.attr(null),
      unterschrifts: this.attr([]),
      auftraggeber: this.attr(null),
      technProjektsteuerungName: this.attr(null),
      technProjektsteuerungUnterschrift: this.attr(null),
      reihenfolge: this.attr(null),
      freigabeDatum: this.attr(null),
      isLocal: this.attr(false),
      hasUnsavedChanges: this.attr(false),
      hasLocalImages: this.attr(false),
      isDownloaded: this.attr(false),
      gutachter: this.attr(null),
      fazit: this.attr(null),
      createdAt: this.attr(null),
      updatedAt: this.attr(null),
      createdBy: this.attr(null),
      updatedBy: this.attr(null),
      bearbeitenderNutzer: this.attr(null),
      bearbeitendesGeraetId: this.attr(null),
      berichts: this.attr(null),
      mangellists: this.attr(null),
      immobilie: this.attr(null),
      // immobilie: this.hasMany(Immobilie, "bestandsaufnahme"),
      fragenblocks: this.attr([]),
      removedMangelsFromMigrations: this.attr(null),
      surveyType: this.attr(null),
      areMinimumFragenblocksSet: this.attr(false),
      vorlage_bestandsaufnahme: this.attr(null),
      geolocationLon: this.attr(null).nullable(),
      geolocationLat: this.attr(null).nullable(),
    };
  }

  public copyJson(): BestandsaufnahmeJson {
    return this.$toJson() as BestandsaufnahmeJson;
  }
}

export const bestandsaufnahme = {
  state: {
    t: {} as any, //importing directly with "import { i18n }  from '@/i18n';"" threw error,
    loaded: false,
    page: 0,
    pageCount: undefined,
    searchResultsCount: null as number | null,
    totalSearchResultsCount: null as number | null,
    sortBy: { label: 'Begehungsdatum', fieldName: 'begehungsdatum', orderBy: 'asc' },
    searchTerm: "",
    stateFilter: [],
    dateFilter: [] as DateFilters,
    persistedBestandsaufnahmes: undefined,
    persistedBestandsaufnahmesLoaded: false,
    isFetchingBestandsaufnahmes: false,
    fetchingBaCancelableSources: [] as CancelTokenSource[],
    exportManagedCall: null as ManagedApiCall | null,
    exportAbortMessage: "Export cancelled by user",
    selectedSurveyCards: new Set<string>(),
    countOfSelectedSurveyCards: 0,
    selectModeSurveyCards: SelectModeState.NONE
  },
  // mutations defined for completeness
  // however: mutations can also be committed by BestandsaufnahmeModel.commit(state => state.example = 'value')
  mutations: {
    setLoaded(state: any) {
      state.loaded = true;
    },
    setPage(state: any, page: number) {
      state.page = page;
    },
    setPageCount(state: any, pageCount: number) {
      state.pageCount = pageCount;
    },
    setSearchResultsCount(state: any, count: number) {
      state.searchResultsCount = count;
    },
    setTotalSearchResultsCount(state: any, totalCount: number) {
      state.totalSearchResultsCount = totalCount;
    },
    setIsFetchingBestandsaufnahmes(state: any, isFetching: boolean) {
      state.isFetchingBestandsaufnahmes = isFetching;
    },
    addFetchingBaCancelableSource(state: any, source: CancelTokenSource) {
      state.fetchingBaCancelableSources.push(source);
    },
    setExportManagedCall(state: any, managedCall: ManagedApiCall | null) {
      state.exportManagedCall = managedCall;
    },
    seti18n(state: any, t: any) {
      state.t = t;
    },
    setSelectedSurveyCards(state: any, selectedSurveyCards: Set<string>) {
      state.selectedSurveyCards = selectedSurveyCards;
      state.countOfSelectedSurveyCards = selectedSurveyCards.size;
    },
    toggleSelectedSurveyCard(state: any, surveyId: string) {
      if (state.selectedSurveyCards.has(surveyId)) {
        state.selectedSurveyCards.delete(surveyId);
      } else {
        state.selectedSurveyCards.add(surveyId);
      }
      state.countOfSelectedSurveyCards = state.selectedSurveyCards.size;
    },
    setSelectModeSurveyCards(state: any, selectMode: SelectModeState) {
      state.selectModeSurveyCards = selectMode;
    }
  },
  actions: {
    setSortBy: ({ state }: { state: any; }, sortBy: string) => {
      state.sortBy = sortBy;
    },
    setSearchTerm: ({ state }: { state: any; }, searchTerm: string) => {
      state.searchTerm = searchTerm;
    },
    setStateFilter: ({ state }: { state: any; }, stateFilter: string) => {
      state.stateFilter = stateFilter;
    },
    setDateFilter: ({ state }: { state: any; }, dateFilter: DateFilters) => {
      state.dateFilter = dateFilter;
    },
    loadPersistedBestandsaufnahmes: async ({ state }: { state: any; }) => {
      const { bestandsaufnahmes } = await BestandsaufnahmeModel.dispatch("$fetchFromLocal");
      return bestandsaufnahmes || [];
    },

    storeMoverSurvey: async ({ state, rootState, commit }: any, surveyJson: BestandsaufnahmeJson) => {
      Monitoring.consoleOnly("storeMoverSurvey", surveyJson);
      if (surveyJson.isDownloaded) {
        await BestandsaufnahmeModel.dispatch('$updateLocally', { data: surveyJson });

      } else {
        await BestandsaufnahmeModel.insertOrUpdate({ data: surveyJson });
      }
    },
    fetchMoverSurveyById: async ({ dispatch }: any, settings: { surveyId: string, saveToStore: boolean; }) => {
      const res: any = await apiClient.get(`/bestandsaufnahmes/${settings.surveyId}`);
      if (settings.saveToStore) {
        res.data.data.isDownloaded = true;
        dispatch('storeMoverSurvey', res.data.data);
      }
      return res.data.data;
    },
    fetchMoverSurveys: async ({ state }: { state: any; }) => {
      Monitoring.consoleOnly("fetchMoverSurveys");
    },
    markAlsoCurrentBestandsaufnahmeObjectAsUnsaved: async (context: any, ba: Bestandsaufnahme) => {
      // this handles an edge case where it is not enough to update the bestandsaufnahme/survey in the store
      // e.g. Duplicate/Delete in RecursiveForm.vue requires to create a new survey object because the connection to the store object is lost
      // the new object has to receive the changes too (in this case hasUnsavedChanges = true) otherwise it could overwrite the store object again
      ba.hasUnsavedChanges = true;
    },
    markWithUnsavedChanges: async ({ dispatch }: any, ba: Bestandsaufnahme) => {
      await dispatch('markAlsoCurrentBestandsaufnahmeObjectAsUnsaved', ba);
      const updatedBaJson = await ba.toClassJson();
      updatedBaJson.hasUnsavedChanges = true;
      dispatch('storeMoverSurvey', updatedBaJson);
    },
    getNextBaQueryString: async ({ state }: any, count: number | void) => {
      BestandsaufnahmeModel.commit((state) => state.page = (BestandsaufnahmeModel.getters("page") + 1));
      const pageSize = count;
      const sortBy = BestandsaufnahmeModel.getters("sortBy");
      const searchString = BestandsaufnahmeModel.getters("searchTerm");
      const stateFilters = BestandsaufnahmeModel.getters("stateFilter");
      const dateFilter = BestandsaufnahmeModel.getters("dateFilter") as DateFilters;
      const page = BestandsaufnahmeModel.getters("page");
      return getBaQuery({
        page,
        pageSize,
        sortBy,
        searchString,
        stateFilters,
        dateFilter
      });
    },
    loadBestandsaufnahmens: async ({ dispatch, commit, state }: any, loadAll = false) => {
      Monitoring.consoleOnly("load Bestandsaufnahmes");
      if (state.isFetchingBestandsaufnahmes) {
        Monitoring.consoleOnly("SOURCE CANCEL");

        state.fetchingBaCancelableSources.forEach((el: CancelTokenSource) => {
          el.cancel('Operation canceled by the user.');
        });
      }

      // NOTE: in offline-case search result counts are not set here in the composable
      BestandsaufnahmeModel.commit((state) => {
        state.searchResultsCount = null;
        state.totalSearchResultsCount = null;
      });

      const persistedBas = await BestandsaufnahmeModel.dispatch("loadPersistedBestandsaufnahmes");
      const stateFilter = BestandsaufnahmeModel.getters("stateFilter");
      try {
        const status = await Network.getStatus();
        if (!status.connected || stateFilter.find((filter: any) => filter.id === HzbaStatusCode.OFFLINE)) {
          if (!status.connected) {
            Monitoring.consoleOnly(`Loading Bestandsaufnahmes failed: Network Error`);
          }

          await BestandsaufnahmeModel.insert({ data: persistedBas });
          BestandsaufnahmeModel.commit((state) => {
            state.page = 1;
            state.pageCount = 1;
            state.loaded = true;
          });
        } else {

          commit('setIsFetchingBestandsaufnahmes', true);
          const CancelToken = axios.CancelToken;
          const source = CancelToken.source();
          commit('addFetchingBaCancelableSource', source);

          const query = await dispatch('getNextBaQueryString', loadAll ? undefined : surveysPageSize);
          const res = await apiClient.get(`/bestandsaufnahmes?${query}`, {
            cancelToken: source.token
          });

          const fetchedBackendBaRes = (res.data as any);
          const pagination = fetchedBackendBaRes.pagination;

          const retrievedCount = pagination.page >= pagination.pageCount ? pagination.total : pagination.page * pagination.pageSize;
          BestandsaufnahmeModel.commit((state) => {
            state.searchResultsCount = retrievedCount;
            state.totalSearchResultsCount = pagination.total;
          });

          // Ba's to push.
          const basToPush: BestandsaufnahmeModel[] = [];

          for (const ba of fetchedBackendBaRes.results) {
            await Immobilie.dispatch('addToFallbackProperties', ba.immobilie);

            ba.immobilie = ba.immobilie?.id;

            // Bas with local edits won't be updated.
            const persistedEditedBa = persistedBas?.find((local: any) => local.id === ba.id && local.isLocal);
            if (persistedEditedBa) {
              basToPush.push(persistedEditedBa);
              continue;
            }

            // Downloaded bas will be updated, but fully instead of preview only.
            const localDownloadedUneditedBa = persistedBas?.find((local: any) => local.id === ba.id);
            if (localDownloadedUneditedBa) {
              if (new Date(localDownloadedUneditedBa!.updatedAt) < new Date(ba.updatedAt)) {
                // new update found from backend. load full ba for this.
                // Monitoring.consoleOnly("DOWNLOAD BA", ba.id)
                const newDownloadedBa = await BestandsaufnahmeModel.dispatch("fetchMoverSurveyById", { surveyId: ba.id, saveToStore: true });

                newDownloadedBa && basToPush.push(newDownloadedBa);
              } else {
                basToPush.push(localDownloadedUneditedBa);
              }
              continue;
            }

            // Ba is neither local edited, nor downloaded and unedited nor should be downloaded
            basToPush.push(ba);
          }
          await BestandsaufnahmeModel.insert({ data: basToPush });

          // Post status updates
          commit('app/updateBestandsaufnahmesLastRefresh', null, { root: true });
          BestandsaufnahmeModel.commit((state) => {
            state.page = pagination.page;
            state.pageCount = pagination.pageCount;
            state.loaded = true;
          });

          commit('setIsFetchingBestandsaufnahmes', false);
        }
      } catch (error: any) {
        Monitoring.chainError("Loading Bestandsaufnahmes failed", error);
      }
    },
    retrieveCompleteSurvey: async ({ dispatch }: any, surveyId: string) => {
      const survey = BestandsaufnahmeModel.query().find(surveyId);
      if (survey?.isDownloaded) {
        return survey;
      } else {
        return dispatch("fetchMoverSurveyById", { surveyId, saveToStore: true });
      }
    },
    getAllSurveyByTemplateId: async ({ rootState }: any, templateUid: string) => {
      const projectId = rootState.user.currentUserProject.id;
      if (rootState.app.networkConnected) {
        return getSurveysByTemplate(templateUid, projectId);
      } else {
        return BestandsaufnahmeModel.getters("moverSurveys")
          .filter((survey: BestandsaufnahmeModel) => survey.vorlage_bestandsaufnahme.uid === templateUid);
      }
    },

    getFeatures: async ({ rootState }: any, survey: Bestandsaufnahme): Promise<Feature[]> => {
      const features: Feature[] = [];
      const config = getGeoJsonExportConfig();
      const immobilie = Immobilie.find(survey.immobilie);

      if (survey.fragenblocks && immobilie) {
        for (const fragenblock of survey.fragenblocks) {
          const geoJsons = await fragenblock.findAllGeoJsons(survey, immobilie, config, rootState.user.organizationUsers);
          if (geoJsons && geoJsons.length > 0) {
            features.push(...geoJsons);
          }
        }
      }

      return features;
    },

    downloadGeodataForPersistedSurvey: async ({ dispatch, state, rootState, rootGetters }: any, surveyInfo: Bestandsaufnahme | string) => {
      let survey: Bestandsaufnahme | null = null;
      try {
        if (typeof surveyInfo === 'string') {
          const foundSurvey = BestandsaufnahmeModel.query().find(surveyInfo);
          if (foundSurvey) {
            survey = new Bestandsaufnahme(foundSurvey.copyJson());
          }
        } else {
          survey = surveyInfo;
        }

        if (!survey) {
          Monitoring.log('downloadGeodataForPersistedSurvey: No survey found for id', typeof surveyInfo === "string" ? surveyInfo : surveyInfo.id);
          return;
        }
        const organisationPrefix = rootGetters['user/organisationPrefix'];
        const t = state.t;
        const filename = `${getFilename(t(`${organisationPrefix}hzba.bestandsaufnahmen`))}-id-${survey.id}.geojson`;
        const features = await dispatch("getFeatures", survey);
        const featCollection = featureCollection(features);
        const jsonStr = JSON.stringify(featCollection, null, 2);
        await downloadFile(jsonStr, filename, 'application/geo+json');

      } catch (error: any) {
        presentToast(rootState.app.t("toasts.downloadGeojson.failed"));
        Monitoring.withScope((scope) => {
          scope.setContext('Download', { "Survey ID": survey?.id ?? surveyInfo });
          Monitoring.chainError("Download of geodata for single survey failed", error);
        });
      }
    },

    downloadGeodataForListOfSurveys: async ({ dispatch, state, rootState, getters, rootGetters }: any) => {
      try {
        const showOfflineOnly = getters.stateFilter?.find((el: any) => el.id === HzbaStatusCode.OFFLINE);
        const handleOfflineCase = rootState.app.isOffline || showOfflineOnly;
        const organisationPrefix = rootGetters['user/organisationPrefix'];
        const t = state.t;
        const filename = `${getFilename(t(`${organisationPrefix}hzba.bestandsaufnahmen`))}.geojson`;
        const selectMode = state.selectModeSurveyCards;

        if (handleOfflineCase) {
          // In offline case we only have persisted surveys
          let surveys: BestandsaufnahmeModel[];
          if (selectMode === SelectModeState.ALL) {
            surveys = getters.persistedBestandsaufnahmes as BestandsaufnahmeModel[];
          } else {
            const surveyIds = getters.selectedCardsIdArray;
            surveys = getters.persistedBestandsaufnahmes.filter((el: BestandsaufnahmeModel) => surveyIds.includes(el.id));
          }
          const features: Feature[] = await Promise.all(surveys.map((survey: BestandsaufnahmeModel) => dispatch("getFeatures", new Bestandsaufnahme(survey.copyJson()))));
          const featCollection = featureCollection([...features]);
          const jsonStr = JSON.stringify(featCollection, null, 2);
          await downloadFile(jsonStr, filename, 'application/geo+json');
        } else {
          let response;
          const callOptions = {
            failHandlers: {
              noErrorOnUserCancelled: true
            }
          };

          const config = getGeoJsonExportConfig();
          if (selectMode === SelectModeState.ALL) {
            const stateFilters = BestandsaufnahmeModel.getters("stateFilter");
            const dateFilter = BestandsaufnahmeModel.getters("dateFilter") as DateFilters;
            const searchString = BestandsaufnahmeModel.getters("searchTerm");
            const filter = getBaFilters({
              stateFilters,
              dateFilter,
              searchString,
            });
            const managedGeoJsonCall = manageApiCall(getGeojsonByFilter, callOptions);
            BestandsaufnahmeModel.commit(state => state.exportManagedCall = managedGeoJsonCall);
            response = await managedGeoJsonCall.makeRequest({ filter, config });
          } else {
            const surveyIds = getters.selectedCardsIdArray;
            const managedGeoJsonCall = manageApiCall(getGeojsonByIds, callOptions);
            BestandsaufnahmeModel.commit(state => state.exportManagedCall = managedGeoJsonCall);
            response = await managedGeoJsonCall.makeRequest({ ids: surveyIds, config });
          }

          if (response?.data) {
            const features: Feature[] = response.data;
            const featCollection = featureCollection(features);
            const jsonStr = JSON.stringify(featCollection, null, 2);
            await downloadFile(jsonStr, filename, 'application/geo+json');
          }
        }
      } catch (error: any) {
        if (error.message !== state.exportAbortMessage) {
          presentToast(rootState.app.t("toasts.downloadGeojson.failed"));
          Monitoring.withScope((scope) => {
            Monitoring.chainError("Download of geodata for list of surveys failed", error);
          });
        }
      }
    },

    abortExport: ({ state }: any) => {
      if (state.exportManagedCall) {
        (state.exportManagedCall as ManagedApiCall).cancelRequest(state.exportAbortMessage);
        BestandsaufnahmeModel.commit(state => state.exportManagedCall = null);
      }
    },

    selectAllSurveyCards({ commit, getters }: any) {
      commit('setSelectModeSurveyCards', SelectModeState.ALL);
      commit("setSelectedSurveyCards", new Set(getters.moverSurveysIds));
    },

    deselectAllSurveyCards({ commit }: any) {
      commit('setSelectModeSurveyCards', SelectModeState.NONE);
      commit("setSelectedSurveyCards", new Set<string>());
    },

    toggleSurveyCardSelection({ state, commit }: any, surveyId: string) {
      commit('toggleSelectedSurveyCard', surveyId);
      commit('setSelectModeSurveyCards', SelectModeState.MANUAL);

      if (state.selectedSurveyCards.size === 0) {
        commit('setSelectModeSurveyCards', SelectModeState.NONE);
      } else if (state.selectedSurveyCards.size === state.totalSearchResultsCount) {
        commit("setSelectModeSurveyCards", SelectModeState.ALL);
      }
    }
  },
  getters: {
    loaded: (state: any) => state.loaded,
    page: (state: any) => state.page,
    pageCount: (state: any) => state.pageCount,
    sortBy: (state: any) => state.sortBy,
    searchTerm: (state: any) => state.searchTerm,
    stateFilter: (state: any) => state.stateFilter,
    dateFilter: (state: any) => state.dateFilter,
    persistedBestandsaufnahmes: (state: any) => BestandsaufnahmeModel.query().where('isDownloaded', true).get(),
    searchFilter: (state: any, getters: any) => (bestandsaufnahme: BestandsaufnahmeModel) => {
      const appliedStateFilters = getters.stateFilter?.filter((el: any) => !el.id.startsWith('LOCALE_'));

      const dateFilters = getters.dateFilter as DateFilters;
      const isInDateRange = dateFilters.every((dateFilter) => {
        if (dateFilter.option === "begehungsdatum") {
          return isDateInRange(bestandsaufnahme.begehungsdatum, dateFilter.value);
        }
        if (dateFilter.option === "sichtungsdatum") {
          return isDateInRange(bestandsaufnahme.sichtungsdatum, dateFilter.value);
        }
        return false;
      });

      const isStatusFilterActive = !appliedStateFilters || appliedStateFilters.length === 0 ||
        appliedStateFilters.find((el: any) => el.id === bestandsaufnahme.status);

      const property = Immobilie.find(bestandsaufnahme.immobilie);

      // this filter runs both in online and offline case
      // in online case the search was already done on the backend
      // only redo the search on client if there is the corresponding property in the store (property !== null)
      // => otherwise correct search results are filtered out
      // TODO: this is needed at the moment because the persisted surveys are always part of this list
      // the search filter could be applied separately in future and only the results joined
      if (property) {
        const isSearchTermFound = (!getters.searchTerm || searchImmobilieByTerm(property, getters.searchTerm));
        return isSearchTermFound && isStatusFilterActive && isInDateRange;
      } else {
        return isStatusFilterActive && isInDateRange;
      }
    },
    sortAndMerge: (state: any, getters: any) => (array: BestandsaufnahmeModel[]) => {
      return array
        .filter(getters.searchFilter)
        .sort((a: BestandsaufnahmeModel, b: BestandsaufnahmeModel) =>
          sortArrayByProperty(
            copyBaAndMergeWithImmobilie(a),
            copyBaAndMergeWithImmobilie(b),
            getters.sortBy
          )
        );
    },
    moverSurveys: (state: any, getters: any, rootState: any, rootGetters: any) => {
      const isLocaleReset = getters.stateFilter.some((item: any) => item.id === HzbaStatusCode.RESET);
      const showOfflineOnly = getters.stateFilter?.find((el: any) => el.id === HzbaStatusCode.OFFLINE);
      const handleOfflineCase = rootGetters['app/isOffline'] || showOfflineOnly;

      if (!handleOfflineCase) {
        let surveys = BestandsaufnahmeModel.all();

        if (getters.stateFilter.length > 0 && !isLocaleReset) {
          // Apply state filter if not reset
          surveys = surveys.filter((el: BestandsaufnahmeModel) =>
            getters.stateFilter.some((item: any) => item.id === el.status)
          );
        }

        // Apply sorting and merging
        return getters.sortAndMerge(surveys, getters.sortBy);
      } else {
        // Handle offline case
        return getters.sortAndMerge(getters.persistedBestandsaufnahmes, getters.searchFilter, getters.sortBy);
      }
    },
    moverSurveysJson: (state: any, getters: any) => {
      return getters.moverSurveys.map((el: BestandsaufnahmeModel) => new Bestandsaufnahme(el.copyJson()));
    },
    moverSurveysIds: (state: any, getters: any) => {
      return getters.moverSurveys.map((el: BestandsaufnahmeModel) => el.id);
    },
    selectedCardsIdArray: (state: any) => {
      return Array.from(state.selectedSurveyCards);
    }
  },
};
